import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'

import { Box, FormLabel, Grid, makeStyles } from '@material-ui/core'

import { BootstrapInput } from './BootstrapInput'

import { pluralize } from '../common/stringUtility'
import { OrderShipmentInfo } from '../common/labelsUtil'
import { formatDateOnly } from '../common/dateTimeUtility'

/**
 * @property {OrdersAtDateCount[]} daysCounts dates must be unique
 */
export interface LabelsPostDateFiltersProps {
  overallCount: number,

  shipmentDateInfo: OrderShipmentInfo,

  onFilter(date: Date | null): void;
}

// TODO: Make these values reasonable, based on Date.now
export const minShipmentDate = new Date('2000')

export const maxShipmentDate = new Date('3000')

/**
 * List of buttons for filtering post's labels
 */
export const LabelsPostDateFilters =
  observer(
    ({overallCount, shipmentDateInfo, onFilter}: LabelsPostDateFiltersProps) => {
      const styles = useLabelPostDateStyles()

      const [isFiltersActive, setFiltersActive] = useState<boolean[]>([])

      const {daysCounts, laterCount, beforeCount} = shipmentDateInfo

      const pluralizeShipments =
        (count: number): string => pluralize(count, '$ отправление', '$ отправления', '$ отправлений')

      useEffect(() => {
        const blankArray = new Array(daysCounts.length + 3).fill(false)
        blankArray[0] = true
        setFiltersActive(blankArray)
      }, [daysCounts])

      /**
       * Changing filter button class to active
       */
      const onFilterSelect =
        (filterIndex: number): void => {
          setFiltersActive(
            isFiltersActive.map((_, i) => i === filterIndex)
          )
        }

      const onFilterClick =
        (date: Date | null, indexToSelect: number): void => {
          if (!isFiltersActive[indexToSelect]) {
            onFilter(date)
            onFilterSelect(indexToSelect)
          }
        }

      const getFilterClassName =
        (filterIndex: number): string | undefined =>
          isFiltersActive[filterIndex] ? styles.selected : undefined

      return (
        <Grid className={styles.container} item container xs={12} spacing={1}>
          <Grid item xs={'auto'}>

            <Box>
              <FormLabel component="legend" className={styles.label}>
                <span className={styles.dash}>—</span>
                Все отправления
              </FormLabel>
            </Box>

            <BootstrapInput
              onClick={() => onFilterClick(null, 0)}
              value={pluralizeShipments(overallCount)}
              className={styles.input}
              inputProps={{className: getFilterClassName(0)}}
            />

          </Grid>

          {
            daysCounts.length > 0 && beforeCount > 0 &&
            <Grid item xs={'auto'}>

              <Box>
                <FormLabel component="legend" className={styles.label}>
                  <span className={styles.dash}>—</span>
                  Ранее
                </FormLabel>
              </Box>

              <BootstrapInput
                onClick={() => onFilterClick(minShipmentDate, 1)}
                value={pluralizeShipments(beforeCount)}
                className={styles.input}
                inputProps={{className: getFilterClassName(1)}}
              />

            </Grid>
          }

          {
            daysCounts.map(({date, count}, i) =>
              <Grid key={date.valueOf()} item xs={'auto'}>

                <Box>
                  <FormLabel component="legend" className={styles.label}>
                    <span className={styles.dash}>—</span>
                    {formatDateOnly(date)}
                  </FormLabel>
                </Box>

                <BootstrapInput
                  onClick={() => onFilterClick(date, i + 2)}
                  value={pluralizeShipments(count)}
                  className={styles.input}
                  inputProps={{className: getFilterClassName(i + 2)}}
                />

              </Grid>)
          }

          {
            daysCounts.length > 0 && laterCount > 0 &&
            <Grid item xs={'auto'}>

              <Box>
                <FormLabel component="legend" className={styles.label}>
                  <span className={styles.dash}>—</span>
                  Позднее
                </FormLabel>
              </Box>

              <BootstrapInput
                onClick={() => onFilterClick(maxShipmentDate, isFiltersActive.length - 1)}
                value={pluralizeShipments(laterCount)}
                className={styles.input}
                inputProps={{className: getFilterClassName(isFiltersActive.length - 1)}}
              />

            </Grid>
          }
        </Grid>
      )
    })

export const useLabelPostDateStyles = makeStyles(
  () => ({
    filter_Button: {
      textTransform: 'none'
    },
    container: {
      marginTop: 10
    },
    selected: {
      backgroundColor: '#E7F4FF !important',
      color: '#4A7DAC !important'
    },
    input: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '14.4px !important',
      textAlign: 'left',
      '& .Mui-disabled': {
        color: '#1F364D'
      },
      caretColor: 'transparent',
      width: 'fit-content'
    },
    dash: {
      color: '#3987CF',
      marginRight: 3
    },
    label: {
      '&, &.MuiFormLabel-root': {
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '14.4px',
        textAlign: 'left',
        marginBottom: 15
      }
    }
  }))